/* ──────────────────────────────────────────────
 *  Snapshot du tunnel course pendant un paiement.
 *  Un moyen à redirection (Klarna) quitte la page :
 *  au retour (Stripe ou bouton Retour du navigateur),
 *  la page est rechargée et le trajet, le devis et le
 *  récapitulatif seraient perdus. Stocké en
 *  sessionStorage (onglet courant, pas de fuite vers
 *  une autre visite), indexé par le pro_payment.
 * ────────────────────────────────────────────── */

import type {
  AddressObject,
  RideCheckoutResponseObject,
  RidePriceCalculatorResponseObject,
} from '@cfixe-monorepo/shared';
import type { RideTypeSelection } from '../types';
import type { CustomFieldAnswer } from '../../booking/types';

const STORAGE_PREFIX = 'propelsia_ride_payment:';
/* Paiement ouvert de l'onglet : un retour arrière (sans paramètre Stripe) le reprend au lieu de
   recréer une course — la course en attente bloque son véhicule et son chauffeur. */
const OPEN_PAYMENT_KEY = 'propelsia_ride_open_payment';

export interface RidePaymentSnapshot {
  /** Pro de la course : un autre tunnel de l'onglet (autre pro) ne la reprend pas. */
  professionalId: number;
  checkoutResult: RideCheckoutResponseObject;
  quote: RidePriceCalculatorResponseObject | null;
  /** Véhicule / formule choisis : le devis et tout re-checkout en dépendent. Absent des snapshots anciens. */
  selection?: RideTypeSelection;
  pickup?: AddressObject;
  dropoff?: AddressObject;
  pickupDatetime: string;
  returnDatetime: string;
  tripType: 'ONE_WAY' | 'ROUND_TRIP';
  passengers: number;
  luggage: number;
  /** Absents des paiements ouverts avant la distinction petits/grands bagages. */
  smallLuggage?: number;
  largeLuggage?: number;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  note: string;
  /** Réponses aux champs personnalisés (re-checkout après « Modifier mes informations »). Absent des snapshots anciens. */
  customAnswers?: CustomFieldAnswer[];
}

/** Enregistre la saisie du paiement `paymentUuid` et en fait le paiement ouvert de l'onglet. */
export function saveRidePaymentSnapshot(paymentUuid: string, snapshot: RidePaymentSnapshot): void {
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${paymentUuid}`, JSON.stringify(snapshot));
    sessionStorage.setItem(OPEN_PAYMENT_KEY, paymentUuid);
  } catch {
    // Storage unavailable (private mode, quota): the return falls back to the payment alone.
  }
}

export function loadRidePaymentSnapshot(paymentUuid: string): RidePaymentSnapshot | undefined {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${paymentUuid}`);
    if (!raw) return undefined;
    const snapshot = JSON.parse(raw) as RidePaymentSnapshot;
    return snapshot.checkoutResult?.payment_uuid === paymentUuid ? snapshot : undefined;
  } catch {
    return undefined;
  }
}

/** Paiement ouvert de l'onglet (voir OPEN_PAYMENT_KEY), s'il a encore son snapshot. */
export function loadOpenRidePayment(): { paymentUuid: string; snapshot: RidePaymentSnapshot } | undefined {
  try {
    const paymentUuid = sessionStorage.getItem(OPEN_PAYMENT_KEY);
    const snapshot = paymentUuid ? loadRidePaymentSnapshot(paymentUuid) : undefined;
    return paymentUuid && snapshot ? { paymentUuid, snapshot } : undefined;
  } catch {
    return undefined;
  }
}

/* Tunnel qui reprend le paiement ouvert : un seul par page. Plusieurs tunnels course peuvent y être
   montés (widget embarqué inline + modal) et partagent le sessionStorage de l'onglet : sans ce verrou,
   chacun rouvrirait le même paiement (formulaire Stripe en double, confirmation en double). */
let openPaymentOwner: object | undefined;

/** Réserve la reprise du paiement ouvert à `owner` ; false si un autre tunnel de la page la tient. */
export function claimOpenRidePayment(owner: object): boolean {
  if (openPaymentOwner !== undefined && openPaymentOwner !== owner) return false;
  openPaymentOwner = owner;
  return true;
}

/** Tunnel démonté : un tunnel monté ensuite (changement d'onglet) peut reprendre le paiement. */
export function releaseOpenRidePayment(owner: object): void {
  if (openPaymentOwner === owner) openPaymentOwner = undefined;
}

/** Oublie le paiement (réglé, annulé ou expiré) : plus rien à reprendre. */
export function clearRidePaymentSnapshot(paymentUuid: string): void {
  try {
    sessionStorage.removeItem(`${STORAGE_PREFIX}${paymentUuid}`);
    if (sessionStorage.getItem(OPEN_PAYMENT_KEY) === paymentUuid) sessionStorage.removeItem(OPEN_PAYMENT_KEY);
  } catch {
    // Silently ignore
  }
}
